<template>
  <form v-if="props.form" class="mb-4">
    <div class="mb-3">
      <span class="heading">{{
        t('loginRegister.user_info_your_informations')
      }}</span>
    </div>
    <div id="gender" class="mb-3">
      <RadioGroup
        class="d-flex flex-row nowrap"
        style="width: 250px; gap: 10px"
        :options="tabOptions"
        :selected-option="selected_gender"
        @radioClick="option => handleGenderChange(option)"
      >
        <template #default="{ option }">
          {{ option.label }}
        </template>
      </RadioGroup>
      <FieldError
        v-if="props.showErrors && props.invalidFields.has('gender')"
        :text="t('user_info_mandatory_field')"
      />
    </div>

    <div class="mb-3">
      <TextInput
        :disabled="isFirstNameDisabled"
        :id="'first_name'"
        :autocomplete="'given-name'"
        :value="props.form.first_name"
        :placeholder="contentProcessed['first_name']"
        :container-box-border="
          props.showErrors && props.invalidFields.has('first_name')
            ? '1px solid var(--primary-orange)'
            : undefined
        "
        @input="newVal => handleTextInputChange('first_name', newVal)"
      >
        <template #after>
          <div
            v-if="!invalidFields.has('first_name') && props.form.first_name"
            class="mx-3 my-2"
          >
            <CheckSvg
              :height="16"
              :width="16"
              :color="'var(--primary-green)'"
            />
          </div>
        </template>
      </TextInput>
      <FieldError
        v-if="props.showErrors && props.invalidFields.has('first_name')"
        :text="t('user_info_mandatory_field')"
      />
    </div>

    <div class="mb-3">
      <TextInput
        :disabled="isLastNameDisabled"
        :id="'last_name'"
        :autocomplete="'family-name'"
        :value="props.form.last_name"
        :placeholder="contentProcessed['last_name']"
        :container-box-border="
          props.showErrors && props.invalidFields.has('last_name')
            ? '1px solid var(--primary-orange)'
            : undefined
        "
        @input="newVal => handleTextInputChange('last_name', newVal)"
      >
        <template #after>
          <div
            v-if="!invalidFields.has('last_name') && props.form.last_name"
            class="mx-3 my-2"
          >
            <CheckSvg
              :height="16"
              :width="16"
              :color="'var(--primary-green)'"
            />
          </div>
        </template>
      </TextInput>
      <FieldError
        v-if="props.showErrors && props.invalidFields.has('last_name')"
        :text="t('user_info_mandatory_field')"
      />
    </div>
    <div class="mb-2">
      <TextInput
        :id="'email'"
        :autocomplete="'email'"
        :value="
          props.form instanceof RegisterForm
            ? props.form.email
            : loggedInUser?.email
        "
        :placeholder="contentProcessed['email']"
        :input-type="'email'"
        :container-box-border="
          props.showErrors && props.invalidFields.has('email')
            ? '1px solid var(--primary-orange)'
            : undefined
        "
        :disabled="isEmailDisabled || true"
        @input="newVal => handleTextInputChange('email', newVal)"
      >
        <template #after>
          <div
            v-if="!invalidFields.has('email') && props.form.email"
            class="mx-3 my-2"
          >
            <CheckSvg
              :height="16"
              :width="16"
              :color="'var(--primary-green)'"
            />
          </div>
        </template>
      </TextInput>
      <FieldError v-if="props.showErrors && props.invalidFields.has('email')">
        <template v-if="!props.form.email">
          {{ t('user_info_mandatory_field') }}
        </template>
        <template v-else-if="!props.isEmailValid">
          {{ t('contacts_email_is_not_valid') }}
        </template>
      </FieldError>
    </div>

    <div id="connect_witn_other" class="mb-3 d-flex justify-content-end">
      <div class="link-small text-primary-blue">
        <span role="button" @click.stop="handleUseOtherEmail">{{
          t('loginRegister.contacts_other_email')
        }}</span>
      </div>
    </div>

    <div
      v-if="props.form instanceof RegisterForm"
      class="d-flex flex-column flex-md-row"
    >
      <!-- <div class="col-12 col-md-7"> -->
      <div class="col-12">
        <div id="password1" class="mb-3">
          <TextInput
            :id="'password1'"
            :autocomplete="'new-password'"
            :value="props.form.password1"
            :placeholder="contentProcessed['password1']"
            :input-type="state.isSeePassword1 ? 'text' : 'password'"
            :container-box-border="
              props.showErrors && props.invalidFields.has('password1')
                ? '1px solid var(--primary-orange)'
                : undefined
            "
            @input="newVal => handleTextInputChange('password1', newVal)"
          >
            <template #after>
              <div
                v-if="props.form.password1"
                role="button"
                @click="() => handleToggleSeePassword('password1')"
                class="mx-3 my-2"
              >
                <EyeSvg
                  :opacity="state.isSeePassword1 ? 1 : 0.5"
                  :color="'var(--primary-blue)'"
                />
              </div>
            </template>
          </TextInput>
          <FieldError
            v-if="props.showErrors && props.invalidFields.has('password1')"
          >
            <template v-if="!props.form.password1">
              {{ t('user_info_mandatory_field') }}
            </template>
            <template v-else-if="props.form.password1.length < 8">
              {{ t('contacts_password_format_lenght') }}
            </template>
          </FieldError>
        </div>
        <div id="password2" class="mb-3">
          <TextInput
            :id="'password2'"
            :autocomplete="'new-password'"
            :value="props.form.password2"
            :placeholder="contentProcessed['password2']"
            :input-type="state.isSeePassword2 ? 'text' : 'password'"
            :container-box-border="
              props.showErrors &&
              (props.invalidFields.has('password2') ||
                (props.form.password2 &&
                  props.form.password1 !== props.form.password2))
                ? '1px solid var(--primary-orange)'
                : undefined
            "
            @input="newVal => handleTextInputChange('password2', newVal)"
          >
            <template #after>
              <div
                v-if="props.form.password2"
                role="button"
                @click="() => handleToggleSeePassword('password2')"
                class="mx-3 my-2"
              >
                <EyeSvg
                  :opacity="state.isSeePassword2 ? 1 : 0.5"
                  :color="'var(--primary-blue)'"
                />
              </div>
            </template>
          </TextInput>
          <FieldError
            v-if="props.showErrors && props.invalidFields.has('password2')"
          >
            <span
              v-if="props.form.password2 && props.form.password2.length < 8"
            >
              {{ t('contacts_password_format_lenght') }}
            </span>
            <span v-else>
              {{ t('user_info_mandatory_field') }}
            </span>
          </FieldError>
          <FieldError
            v-if="
              props.showErrors &&
              props.form.password2 &&
              props.form.password1 !== props.form.password2
            "
          >
            <span>
              {{ t('contacts_passwords_dont_match') }}
            </span>
          </FieldError>
        </div>
      </div>
      <!-- <div class="col-12 col-md-5">
                <PasswordValidityBox class="px-3" :password="props.form.password1" />
            </div> -->
    </div>
  </form>
</template>

<script lang="ts" setup>
  import {
    reactive,
    computed,
    defineProps,
    defineEmits,
    ComputedRef,
  } from 'vue'

  import RadioGroup from '@/components/LoginRegister/common/input_groups/RadioGroup/v1/RadioGroup.vue'
  import TextInput from '@/components/LoginRegister/common/inputs/Text/v1/Text.vue'
  import FieldError from '@/components/LoginRegister/common/FieldError/v1/FieldError.vue'

  import EyeSvg from '@/components/LoginRegister/svg/EyeSvg.vue'
  import CheckSvg from '@/components/LoginRegister/svg/CheckSvg.vue'
  import register_form_mandatory from '@/config/loginRegisterLegacy/register_form_mandatory.config'
  import RegisterForm from '@/model/Form/RegisterForm'
  import * as _ from 'lodash'
  import { GenderEnum as genderEnum } from '@/services/AuthService.interface'
  import loggedInUser from '@/state/loggedInUser'
  import locale from '@/state/locale'

  // Translations
  import { useI18n } from 'vue-i18n'
  const { t } = useI18n()

  interface Props {
    form: RegisterForm
    invalidFields: Set<string>
    showErrors: boolean
    isEmailValid?: boolean
  }
  interface Emits {
    (e: 'updateForm', form: RegisterForm): void
    (e: 'useOtherEmail'): void
  }

  const tabOptions = [
    {
      label: t('loginRegister.mister'),
      value: genderEnum.Male,
    },
    {
      label: t('loginRegister.madam'),
      value: genderEnum.Female,
    },
  ]

  // state
  const state = reactive({
    isSeePassword1: false,
    isSeePassword2: false,
  })

  // props
  const props = defineProps<Props>()

  // emit
  const emit = defineEmits<Emits>()

  // computed
  const selected_gender = computed(() => {
    return tabOptions.find(
      gender => gender.value === (props.form && props.form.gender)
    )
  })
  const mandatoryFieldsSet = computed(() => {
    if (props.form instanceof RegisterForm) {
      return register_form_mandatory
    } else {
      throw new TypeError('form has to be instance of RegisterForm')
    }
  })
  const contentProcessed = computed(() => {
    // this simply adds a star if the field is mandatory
    return {
      first_name: `${t('loginRegister.contacts_first_name')}${
        mandatoryFieldsSet.value.has('first_name') ? '*' : ''
      }`,
      last_name: `${t('loginRegister.contacts_last_name')}${
        mandatoryFieldsSet.value.has('last_name') ? '*' : ''
      }`,
      email: `${t('loginRegister.contacts_email')}${
        mandatoryFieldsSet.value.has('email') ? '*' : ''
      }`,
      password1: `${t('loginRegister.contacts_password1')}${
        mandatoryFieldsSet.value.has('password1') ? '*' : ''
      }`,
      password2: `${t('loginRegister.contacts_password2')}${
        mandatoryFieldsSet.value.has('password2') ? '*' : ''
      }`,
    }
  })

  // handlers
  const handleRadioClick = (option: any) => {
    const formClone = _.cloneDeep(props.form)
    formClone.gender = option.value
    emit('updateForm', formClone)
  }
  const handleTextInputChange = (field: string, newValue: string) => {
    const fieldKey = field as keyof RegisterForm
    const formClone = _.cloneDeep(props.form) as any // stupid annoying TS error
    formClone[fieldKey] = newValue
    emit('updateForm', formClone)
  }
  const handleToggleSeePassword = (ref: any) => {
    if (ref === 'password1') state.isSeePassword1 = !state.isSeePassword1
    if (ref === 'password2') state.isSeePassword2 = !state.isSeePassword2
  }
  const handleGenderChange = (option: any) => {
    const formClone = _.cloneDeep(props.form)
    formClone.gender = option.value
    emit('updateForm', formClone)
  }
  const handleUseOtherEmail = () => {
    emit('useOtherEmail')
  }
</script>

<style scoped></style>
